/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import type {
	CalendarIssueEventFields,
	CalendarScopePlan,
	CalendarViewRange,
	CalendarWeekStart,
	CalendarColorBy,
} from '@jira/calendar__calendar-view/src/controllers/calendar-store/types.tsx';
import parameters from '@jira/calendar__calendar-view/src/ui/__generated__/CalendarContentQuery$parameters';
import type {
	JiraViewScopeInput,
	JiraCalendarViewConfigurationInput,
	JiraCalendarIssuesInput,
	JiraCalendarVersionsInput,
	JiraCalendarSprintsInput,
	JiraCalendarMode,
	JiraCalendarWeekStart,
	JiraCalendarCrossProjectVersionsInput,
} from '@jira/calendar__calendar-view/src/ui/__generated__/CalendarContentQuery.graphql';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import type { RouterContext } from '@atlassian/react-resource-router';
import type { CalendarSettingsQueryParams, Queries, ExtraProps } from './OnCreatePayload.types.tsx';

const ROUTE_NAMES_SOFTWARE_PLAN_CALENDAR_EMBED = 'software-plan-calendar-embed';

type CalendarViewSettings = {
	weekStartsOn: CalendarWeekStart;
	viewRange: CalendarViewRange;
};

const SETTINGS_STORAGE_KEY = 'plans_calendar_view_settings';
function getViewSettings(key: string): CalendarViewSettings | undefined {
	try {
		// eslint-disable-next-line jira/web-storage/no-direct-web-storage-usage
		const serializedValue = globalThis.localStorage?.getItem(key);

		return serializedValue !== null
			? // oxlint-disable-next-line typescript/consistent-type-assertions
				(JSON.parse(serializedValue) as CalendarViewSettings)
			: undefined;
	} catch {
		return undefined;
	}
}

function toPlanScenarioAri({
	siteId,
	activationId,
	planId,
	scenarioId,
}: {
	siteId: string;
	activationId: string;
	planId: string;
	scenarioId: string;
}) {
	return `ari:cloud:jira:${siteId}:plan-scenario/activation/${activationId}/${planId}/scenario/${scenarioId}`;
}

function getStorageKeyFromScope(context: RouterContext): string {
	const {
		match: {
			params: { planId, scenarioId },
		},
	} = context;
	const LOCALSTORAGE_KEY_PREFIX = 'persist:calendar';
	return `${LOCALSTORAGE_KEY_PREFIX}/plan/${planId}/scenario/${scenarioId}`;
}

function getStorageValueFromScope(key: string): string {
	// eslint-disable-next-line jira/web-storage/no-direct-web-storage-usage
	const storedData = globalThis.localStorage?.getItem(key) || '';
	return storedData;
}

function toMidnightUTCString(date: Date) {
	const utcMilliseconds = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
	return new Date(utcMilliseconds).toISOString();
}

function toDate(date: string | null | undefined): Date {
	if (!date) {
		return new Date();
	}
	try {
		const _date = new Date(date);
		return new Date(_date.getFullYear(), _date.getMonth(), _date.getDate());
	} catch (e) {
		return new Date();
	}
}

function createScope({ context, tenantContext }: EntryPointRouteParams): CalendarScopePlan {
	const {
		match: {
			params: { planId, scenarioId },
		},
	} = context;
	const { cloudId, activationId } = tenantContext;

	const planScenarioAri = toPlanScenarioAri({
		siteId: cloudId,
		activationId,
		planId: String(planId),
		scenarioId: String(scenarioId),
	});

	return {
		type: 'plan',
		ids: [planScenarioAri],
	};
}

function buildScopeInput(scope: CalendarScopePlan): JiraViewScopeInput {
	return {
		ids: scope.ids,
	};
}

function buildViewId(scope: CalendarScopePlan): string {
	// plan scenario ARI is the view id
	return scope.ids?.[0] ?? '';
}

function toViewMode(mode: string): JiraCalendarMode {
	switch (mode) {
		case 'week':
			return 'WEEK';
		case 'day':
			return 'DAY';
		default:
			return 'MONTH';
	}
}

function toWeekStart(weekStart: string): JiraCalendarWeekStart {
	switch (weekStart) {
		case 'monday':
			return 'MONDAY';
		case 'saturday':
			return 'SATURDAY';
		default:
			return 'SUNDAY';
	}
}

function toCalendarWeekStart(weekStart: string): CalendarWeekStart {
	switch (weekStart) {
		case 'sunday':
			return 'sunday';
		case 'saturday':
			return 'saturday';
		default:
			return 'monday';
	}
}

function toCalendarColorBy(colorBy: string): CalendarColorBy {
	switch (colorBy) {
		case 'none':
			return 'none';
		case 'status':
			return 'status';
		default:
			return 'status';
	}
}

function toCalendarViewRange(view: string): CalendarViewRange {
	switch (view) {
		case 'week':
			return 'week';
		default:
			return 'month';
	}
}

function buildConfigurationInput({
	selectedDate,
	viewRange,
	weekStartsOn,
	startDateField,
	endDateField,
	viewId,
}: {
	selectedDate: Date;
	viewRange: CalendarViewRange;
	weekStartsOn: CalendarWeekStart;
	startDateField: string;
	endDateField: string;
	viewId: string;
}): JiraCalendarViewConfigurationInput {
	return {
		date: toMidnightUTCString(selectedDate),
		mode: toViewMode(viewRange),
		weekStart: toWeekStart(weekStartsOn),
		startDateField,
		endDateField,
		viewId,
	};
}

function buildIssuesSearchInput(
	storageKey: string,
	filter: string,
	isEmbed: boolean,
): JiraCalendarIssuesInput {
	return {
		additionalFilterQuery:
			isEmbed === true ? (filter ?? '') : filter || getStorageValueFromScope(storageKey),
	};
}

function buildUnscheduledIssuesSearchInput(): JiraCalendarIssuesInput {
	return { additionalFilterQuery: '' };
}

function buildVersionsSearchInput(): JiraCalendarVersionsInput {
	return { versionStatuses: ['RELEASED', 'UNRELEASED'] };
}

function buildCalendarCrossProjectVersionsInput(): JiraCalendarCrossProjectVersionsInput {
	return { statuses: ['RELEASED', 'UNRELEASED'] };
}

function buildSprintsSearchInput(): JiraCalendarSprintsInput {
	return { sprintStates: ['ACTIVE', 'FUTURE', 'CLOSED'] };
}

function buildSettingsParams(
	hideWeekends: string,
	hideSprints: string,
	hideVersions: string,
	hideIssues: string,
	hideIssueKey: string,
	weekStartsOnQueryParam: string,
	colorBy: string,
	viewRange: string,
): CalendarSettingsQueryParams | undefined {
	if (
		hideWeekends ||
		hideSprints ||
		hideVersions ||
		hideIssues ||
		hideIssueKey ||
		weekStartsOnQueryParam ||
		colorBy ||
		viewRange
	) {
		return {
			hideWeekends: hideWeekends === 'true' || undefined,
			hideSprints: hideSprints === 'true' || undefined,
			hideVersions: hideVersions === 'true' || undefined,
			hideIssues: hideIssues === 'true' || undefined,
			hideIssueKey: hideIssueKey === 'true' || undefined,
			weekStartsOn: toCalendarWeekStart(weekStartsOnQueryParam),
			colorBy: toCalendarColorBy(colorBy),
			viewRange: toCalendarViewRange(viewRange),
		};
	}
	return undefined;
}

type CalendarEntryPointComponent = EntryPointComponent<Queries, {}, {}, ExtraProps>;

export const calendarEntryPoint: EntryPoint<CalendarEntryPointComponent, EntryPointRouteParams> =
	createEntryPoint({
		root: JSResourceForUserVisible<CalendarEntryPointComponent>(
			/* webpackChunkName: "async-plan-calendar-entrypoint" */ './CalendarEntryPoint.tsx',
			{
				entryFsSsrLiftImportToModule: true,
			},
		),
		getPreloadProps: (entryPointRouteParams: EntryPointRouteParams) => {
			const { context, tenantContext } = entryPointRouteParams;
			const { cloudId } = tenantContext;
			const scope = createScope(entryPointRouteParams);
			const scopeInput = buildScopeInput(scope);
			const {
				date,
				filter,
				hideWeekends,
				hideSprints,
				hideVersions,
				hideIssues,
				hideIssueKey,
				weekStartsOn: weekStartsOnQueryParam,
				colorBy: colorByQueryParam,
				viewRange: viewRangeParam,
			} = context.query;
			const selectedDate = toDate(date);
			const hasFilterQueryParam = Boolean(filter);
			const routeName = context.route.name;
			const settingsQueryParams = buildSettingsParams(
				hideWeekends,
				hideSprints,
				hideVersions,
				hideIssues,
				hideIssueKey,
				weekStartsOnQueryParam,
				colorByQueryParam,
				viewRangeParam,
			);
			const isEmbed = routeName === ROUTE_NAMES_SOFTWARE_PLAN_CALENDAR_EMBED;
			const settings = getViewSettings(SETTINGS_STORAGE_KEY);
			const viewRange: CalendarViewRange =
				settingsQueryParams?.viewRange ?? settings?.viewRange ?? 'month';
			const weekStartsOn = settingsQueryParams?.weekStartsOn ?? settings?.weekStartsOn ?? 'monday';
			const startDateField = 'startdate';
			const endDateField = 'duedate';
			const issueEventFields: CalendarIssueEventFields = {
				startDateField,
				endDateField,
			};
			const storageKey = getStorageKeyFromScope(context);
			const viewId = buildViewId(scope);
			const filterQuery = buildIssuesSearchInput(storageKey, filter, isEmbed);
			return {
				queries: {
					calendarData: {
						options: {
							fetchPolicy: 'network-only' as const,
						},
						parameters,
						variables: {
							cloudId,
							scopeInput,
							configurationInput: buildConfigurationInput({
								selectedDate,
								viewRange,
								weekStartsOn,
								startDateField,
								endDateField,
								viewId,
							}),
							issuesPageSize: 50,
							issuesSearchInput: filterQuery,
							unscheduledIssuesSearchInput: buildUnscheduledIssuesSearchInput(),
							versionsSearchInput: buildVersionsSearchInput(),
							sprintsSearchInput: buildSprintsSearchInput(),
							crossProjectVersionsSearchInput: buildCalendarCrossProjectVersionsInput(),
							viewId,
							skipSprintSearch: false,
							skipPanelIssueSearch: true,
							skipCrossProjectVersionSearch: false,
							skipVersionsV2Search: false,
							includeRecurrenceIndicator: false,
							skipUserPreferences: false,
							isCalendarFilterRefinementEnabled: false,
							filterScope: null,
						},
					},
				},
				entryPoints: {},
				extraProps: {
					scope,
					selectedDate,
					viewRange,
					weekStartsOn,
					issueEventFields,
					storageKey,
					hasFilterQueryParam,
					routeName,
					settingsQueryParams,
					filterQuery: filterQuery.additionalFilterQuery,
					planId: String(context.match.params.planId),
				},
			};
		},
	});
